import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {httpsCallable} from 'firebase/functions';
import {functions} from '../config/firebase';
import {
  detectLanguage,
  languageDisplayName,
  readerLanguage,
  shouldOfferTranslate,
} from '../utils/detectLanguage';
import {getPlainTextFromRichHtml} from '../utils/openGraphUtils';
import logger from '../utils/logger';

const sessionCache = new Map();

function cacheKey(songId, targetLang, plain) {
  return `${songId}:${targetLang}:${plain.length}:${plain.slice(0, 48)}`;
}

/**
 * Click-to-translate for an About this song body. Offers the control when
 * the guessed source language differs from the reader language, then calls
 * `translateSongDescription` (server-side cache + Cloud Translation).
 */
export default function useTranslateDescription(songId, description) {
  const plain = useMemo(
    () => getPlainTextFromRichHtml(description || ''),
    [description]
  );
  const targetLang = useMemo(() => readerLanguage(), []);
  const detected = useMemo(() => detectLanguage(plain), [plain]);
  const [shown, setShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [suppressed, setSuppressed] = useState(false);
  const requestGen = useRef(0);

  useEffect(() => {
    requestGen.current += 1;
    setShown(false);
    setResult(null);
    setError(null);
    setSuppressed(false);
    setLoading(false);
  }, [songId, plain]);

  const offer = useMemo(() => {
    if (suppressed) return false;
    if (!songId || !plain) return false;
    return shouldOfferTranslate(detected, targetLang, plain);
  }, [detected, plain, songId, suppressed, targetLang]);

  const sourceLang = result?.sourceLang || detected || '';
  const sourceName = useMemo(
    () => languageDisplayName(sourceLang, targetLang),
    [sourceLang, targetLang]
  );

  const translate = useCallback(async () => {
    if (!songId) return;
    if (result?.translatedText) {
      setShown(true);
      return;
    }
    const key = cacheKey(songId, targetLang, plain);
    const cached = sessionCache.get(key);
    if (cached) {
      if (cached.sameLanguage) {
        setSuppressed(true);
        setShown(false);
        return;
      }
      setResult(cached);
      setShown(true);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    const gen = requestGen.current;
    try {
      const fn = httpsCallable(functions, 'translateSongDescription');
      const {data} = await fn({songId, targetLang});
      if (gen !== requestGen.current) return;
      if (data?.sameLanguage) {
        setSuppressed(true);
        setShown(false);
        sessionCache.set(key, data);
        return;
      }
      if (!data?.translatedText) {
        throw new Error('Empty translation');
      }
      sessionCache.set(key, data);
      setResult(data);
      setShown(true);
    } catch (err) {
      if (gen !== requestGen.current) return;
      logger.warn('translateSongDescription failed', err);
      setError(err);
    } finally {
      if (gen === requestGen.current) setLoading(false);
    }
  }, [plain, result, songId, targetLang]);

  const showOriginal = useCallback(() => {
    setShown(false);
  }, []);

  return {
    offer,
    shown,
    loading,
    error,
    translatedText: result?.translatedText || '',
    sourceLang,
    sourceName,
    targetLang,
    translate,
    showOriginal,
  };
}
